import type {
  ColumnFiltersState,
  SortingState,
  Updater,
} from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams as useSearchParamsReactRouter } from 'react-router-dom';
import type { Pagination } from '../Table2';

const FILTER_PREFIX = 'f.';
const FILTER_DEBOUNCE_MS = 300;

type UseTable2UrlStateOptions = {
  defaultSorting?: SortingState;
  defaultPageSize?: number;
};

const serializeFilterValue = (value: unknown): string => {
  if (Array.isArray(value)) {
    return value.map(v => (v === null ? '' : String(v))).join(',');
  }

  return String(value ?? '');
};

const deserializeFilterValue = (
  raw: string,
): string | string[] | [string | null, string | null] => {
  if (raw.includes(',')) {
    const parts = raw.split(',');
    // Date-range values contain empty strings for null dates
    const hasNullParts = parts.some(p => p === '');
    if (hasNullParts || parts.length === 2) {
      return parts.map(p => (p === '' ? null : p)) as [
        string | null,
        string | null,
      ];
    }
    return parts;
  }
  return raw;
};

const resolveUpdater = <T>(updaterOrValue: Updater<T>, prev: T): T => {
  if (typeof updaterOrValue === 'function') {
    return (updaterOrValue as (prev: T) => T)(prev);
  }
  return updaterOrValue;
};

export const useTable2UrlState = (options: UseTable2UrlStateOptions = {}) => {
  const { defaultSorting = [], defaultPageSize = 10 } = options;

  const [searchParams, setSearchParams] = useSearchParamsReactRouter();

  // Keep setSearchParams in a ref so callbacks remain referentially stable.
  // react-router may return a new setSearchParams reference on every URL change,
  // which would cause useCallback consumers to re-create and trigger useEffects.
  const setSearchParamsRef = useRef(setSearchParams);
  setSearchParamsRef.current = setSearchParams;

  const defaultSortingRef = useRef(defaultSorting);
  defaultSortingRef.current = defaultSorting;

  // --- Pagination ---

  const pagination: Pagination = useMemo(() => {
    const pageParam = searchParams.get('page');
    const pageSizeParam = searchParams.get('pageSize');

    return {
      pageIndex: pageParam ? Number(pageParam) - 1 : 0,
      pageSize: pageSizeParam ? Number(pageSizeParam) : defaultPageSize,
    };
  }, [searchParams, defaultPageSize]);

  const setPagination = useCallback(
    (updater: Updater<Pagination>) => {
      setSearchParamsRef.current(prev => {
        const currentPagination: Pagination = {
          pageIndex: prev.has('page') ? Number(prev.get('page')) - 1 : 0,
          pageSize: prev.has('pageSize')
            ? Number(prev.get('pageSize'))
            : defaultPageSize,
        };
        const next = resolveUpdater(updater, currentPagination);

        if (next.pageIndex === 0) {
          prev.delete('page');
        } else {
          prev.set('page', String(next.pageIndex + 1));
        }

        if (next.pageSize === defaultPageSize) {
          prev.delete('pageSize');
        } else {
          prev.set('pageSize', String(next.pageSize));
        }

        return prev;
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [defaultPageSize],
  );

  // --- Sorting ---

  const sorting: SortingState = useMemo(() => {
    const sortParam = searchParams.get('sort');
    if (!sortParam) return defaultSorting;

    const desc = sortParam.startsWith('-');
    const id = desc ? sortParam.slice(1) : sortParam;

    return [{ id, desc }];
  }, [searchParams, defaultSorting]);

  const setSorting = useCallback(
    (updater: Updater<SortingState>) => {
      setSearchParamsRef.current(prev => {
        const ds = defaultSortingRef.current;
        const currentSorting: SortingState = (() => {
          const s = prev.get('sort');
          if (!s) return ds;
          const desc = s.startsWith('-');
          return [{ id: desc ? s.slice(1) : s, desc }];
        })();

        const next = resolveUpdater(updater, currentSorting);

        // Reset page when sorting changes
        prev.delete('page');

        if (next.length === 0) {
          prev.delete('sort');
        } else {
          const { id, desc } = next[0];
          const defaultMatch =
            ds.length > 0 && ds[0].id === id && ds[0].desc === desc;

          if (defaultMatch) {
            prev.delete('sort');
          } else {
            prev.set('sort', desc ? `-${id}` : id);
          }
        }

        return prev;
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // --- Column Filters (debounced) ---

  const filtersFromUrl: ColumnFiltersState = useMemo(() => {
    const filters: ColumnFiltersState = [];

    searchParams.forEach((value, key) => {
      if (key.startsWith(FILTER_PREFIX)) {
        const id = key.slice(FILTER_PREFIX.length);
        filters.push({ id, value: deserializeFilterValue(value) });
      }
    });

    return filters;
  }, [searchParams]);

  // Local (instant) filter state — drives input values without lag.
  // Initialised from URL and kept in sync when URL changes externally
  // (e.g. browser back/forward).
  const [localFilters, setLocalFilters] =
    useState<ColumnFiltersState>(filtersFromUrl);

  // Sync local state when URL filters change externally (back/forward nav).
  const filtersFromUrlRef = useRef(filtersFromUrl);
  useEffect(() => {
    const prev = filtersFromUrlRef.current;
    if (prev !== filtersFromUrl) {
      filtersFromUrlRef.current = filtersFromUrl;
      // Only sync when the URL change did NOT originate from our debounce
      // (simple reference check — filtersFromUrl is a new array from useMemo).
      setLocalFilters(filtersFromUrl);
    }
  }, [filtersFromUrl]);

  // Debounce timer ref
  const filterTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flushFiltersToUrl = useCallback((next: ColumnFiltersState) => {
    setSearchParamsRef.current(prev => {
      // Remove all existing filter params
      const keysToDelete: string[] = [];
      prev.forEach((_value, key) => {
        if (key.startsWith(FILTER_PREFIX)) {
          keysToDelete.push(key);
        }
      });
      keysToDelete.forEach(key => {
        prev.delete(key);
      });

      // Reset page when filters change
      prev.delete('page');

      // Set new filter params
      next.forEach(({ id, value }) => {
        const serialized = serializeFilterValue(value);
        if (serialized !== '') {
          prev.set(`${FILTER_PREFIX}${id}`, serialized);
        }
      });

      return prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setColumnFilters = useCallback(
    (updater: Updater<ColumnFiltersState>) => {
      setLocalFilters(prev => {
        const next = resolveUpdater(updater, prev);

        // Cancel previous debounce timer
        if (filterTimerRef.current) {
          clearTimeout(filterTimerRef.current);
        }

        // Schedule URL update
        filterTimerRef.current = setTimeout(() => {
          flushFiltersToUrl(next);
        }, FILTER_DEBOUNCE_MS);

        return next;
      });
    },
    [flushFiltersToUrl],
  );

  // Cleanup timer on unmount
  useEffect(
    () => () => {
      if (filterTimerRef.current) {
        clearTimeout(filterTimerRef.current);
      }
    },
    [],
  );

  return {
    pagination,
    setPagination,
    sorting,
    setSorting,
    /** Instant (local) filters — use for Table2 UI input state. */
    columnFilters: localFilters,
    /** Debounced filters synced with URL — use for API queries. */
    debouncedColumnFilters: filtersFromUrl,
    setColumnFilters,
  };
};
